import React from 'react'
import useStore from 'state/knovStore'
import { focusActiveNewAnswer, focusSideNewAnswer } from 'state/imperativeApis/editorApi'
import questModel from 'components/quests/questModel'
import { cacheQuest, getCachedUser } from 'state/cache'
import { setPendingComposerSeed } from 'state/pendingComposerSeed'
import useStreamFilters from 'components/filters/useStreamFilters'
import { getCenterPanel } from 'state/imperativeApis/swiperApi'
import WithTooltip from 'components/shared/WithTooltip'
import useTeamLookup from 'components/teams/useTeamLookup'
import api from 'api/api'
import { buildUserStreamMentionDelta } from 'components/quests/newThreadUserMention'
import { runMintMediaThreadFlow } from 'components/quests/mintMediaThreadFlow'
import { isOrdinalsMintEnabled } from 'components/quests/mintMediaThreadPolicy'
import { isRadioStreamFilter } from 'components/panels/feedNewItemsEligibility'
import { runNewMusicFlow } from 'components/quests/newMusicFlow'
import { requestChannelJoin } from 'components/teams/ChannelJoinInviteModal'
import { membersOnlyPostReason } from 'state/channelJoinInvite'

export default function NewQuestButton(props) {
    const panelId = props.panelId || props.panel?.panelId
    // Default to insert-and-open behavior unless explicitly disabled.
    const { selectNewFilter } = useStreamFilters(panelId, { append: props.append !== false })
    const openAuthModal = useStore(state => state.actions.modalActions.openAuthModal)
    const shouldShowJoinCta = props.joinOnGuest && !gon?.currentUser
    // In the ordinals streams "new thread" means minting media onchain, not a text composer.
    const isOrdinalsMint = !shouldShowJoinCta && isOrdinalsMintEnabled(props.filter)
    const isNewMusic = !shouldShowJoinCta && isRadioStreamFilter(props.filter)
    const tooltipLabel =
        props.tip ||
        (shouldShowJoinCta
            ? 'Join Treechat'
            : isOrdinalsMint
            ? 'Mint media as an ordinal'
            : isNewMusic
            ? 'New Music'
            : 'New Thread')
    const buttonTestId =
        props.testId ||
        (shouldShowJoinCta
            ? 'join-treechat-button'
            : isOrdinalsMint
            ? 'mint-media-button'
            : isNewMusic
            ? 'new-music-button'
            : 'new-thread-button')
    const explicitFilterTeamId = props.filter?.team_ids?.[0]
    const { team: resolvedTeam } = useTeamLookup(explicitFilterTeamId, props.team)

    const resolveTeamForFilter = async filterTeamId => {
        if (!filterTeamId) return null
        if (resolvedTeam && String(resolvedTeam.id) === String(filterTeamId)) return resolvedTeam

        try {
            const team = await api.getTeam(filterTeamId)
            return team && String(team.id) === String(filterTeamId) ? team : null
        } catch (error) {
            console.warn('Failed to resolve stream permissions before creating thread:', error)
            return null
        }
    }

    const resolveUserForFilter = async filter => {
        const filterUserId = filter?.user
        if (!filterUserId) return null

        const cachedUser = getCachedUser(filterUserId)
        if (cachedUser?.name) return cachedUser

        const storeUser = useStore
            .getState()
            .users?.find(user => String(user.id) === String(filterUserId))
        if (storeUser?.name) return storeUser

        return api.getUser(filterUserId)
    }

    const onClick = async ev => {
        ev.stopPropagation()
        ev.preventDefault()

        if (shouldShowJoinCta) {
            openAuthModal('signup', props.authSource || 'join_cta')
            return
        }

        const panels = useStore.getState().panels
        const panel = panelId ? panels.getPanel(panelId) : getCenterPanel()

        // Use an explicitly provided filter (e.g. from a stream-specific "new thread" button)
        // or fall back to the current panel's filter.
        const newFilter = props.filter ||
            panel?.filter ||
            getCenterPanel()?.filter || { public: true }

        if (isOrdinalsMintEnabled(newFilter)) {
            await runMintMediaThreadFlow({ source: props.authSource || 'new_thread_button' })
            if (props.callback) props.callback()
            return
        }

        // Only the station posts threads in the music channel: New Music picks a
        // song and posts it as a public music thread, which Radio can draw from.
        if (isRadioStreamFilter(newFilter)) {
            await runNewMusicFlow()
            if (props.callback) props.callback()
            return
        }

        if (panel) {
            const filterTeamId = newFilter?.team_ids?.[0]
            const teamForFilter = await resolveTeamForFilter(filterTeamId)
            // A channel only members post in, that anyone may join: ask them to
            // join first instead of opening a thread that would turn private.
            if (questModel.needsChannelJoinToPost(teamForFilter, gon?.currentUser)) {
                requestChannelJoin(teamForFilter, {
                    reasonText: membersOnlyPostReason(teamForFilter.name || 'this channel'),
                    // Same caller cleanup (e.g. closing a drawer) as the normal path.
                    afterJoin: async () => {
                        await openDraft(panel, newFilter, teamForFilter)
                        if (props.callback) props.callback()
                    },
                })
                return
            }
            await openDraft(panel, newFilter, teamForFilter)
        }
        if (props.callback) props.callback()
    }

    const openDraft = async (panel, newFilter, teamForFilter) => {
        const filterUser = await resolveUserForFilter(newFilter)
        const draftQuest = questModel.newQuestFromFilter({}, newFilter, {
            team: teamForFilter,
        })
        const userMentionDelta = buildUserStreamMentionDelta(newFilter, filterUser)
        if (userMentionDelta) {
            setPendingComposerSeed(draftQuest.id, { deltaJson: userMentionDelta })
        }
        cacheQuest(draftQuest)

        selectNewFilter({ questId: draftQuest.id }, { newThread: true, draftFilter: newFilter })
        if (!panelId) {
            history.pushState({}, null, '/new')
        }
    }

    return (
        <WithTooltip tip={tooltipLabel} place={props.place} disable={props.disableTooltip}>
            {React.cloneElement(props.children, {
                onClick,
                'data-testid': buttonTestId,
                // Avoid passing custom camelCase props to native DOM nodes.
                ...(typeof props.children.type === 'string' ? {} : { testId: buttonTestId }),
            })}
        </WithTooltip>
    )
}
